/**
 * One world, running.
 *
 * Everything that belongs to a single map - the blocks, the fire, the raid,
 * the orcs and their arrows, the queue of buildings waiting to go up, and the
 * people standing in it. The server holds several of these and a client
 * belongs to exactly one.
 *
 * **Why a class and not another process.** Routing each map to its own
 * process would mean a subdomain or a path for every one of them, which makes
 * adding a map a job for nginx rather than a line of game code. Choosing a
 * map is part of the game; it should not be an act of system administration.
 * So the world's name travels in `hello`, one address serves all of them, and
 * this is the thing that name picks out.
 *
 * **It knows nothing about sockets.** It is handed people who can be sent
 * messages - that is all `RealmClient` is - so the door, the word and the
 * limits stay in one place and this stays testable.
 *
 * **It is grown on demand.** A world nobody has asked for is a name in a
 * list, costing nothing; the terrain appears the first time somebody joins
 * it, and goes away again when the last of them has been gone long enough.
 * Growing the кремль is about a second and ninety megabytes, and paying that
 * for every map on the server at startup would make adding maps expensive in
 * exactly the way this design is meant to stop.
 */

import { PlainItemId } from "../block/items.js";
import { newToken } from "./token.js";
import { World } from "../world/world.js";
import { CHUNK_SIZE, type Chunk } from "../world/chunk.js";
import type { GameMap } from "../world/maps.js";
import {
  isSolid, isNaturalGround, isWater, isWallTorch, wallTorchFacing,
} from "../block/registry.js";
import { CASTLE_POSTS, CASTLE_THRONE, castleCell } from "../world/castle.js";
import { Fire } from "../world/fire.js";
import { RaidClock, LairClock, MAX_ORCS } from "../world/raid.js";
import {
  makeOrc, stepOrcs, rollOrcDrops, huntable, type Orc, type OrcTarget,
} from "../entity/orc.js";
import {
  makeArrow, stepArrows, ARROW_DAMAGE, type Arrow,
} from "../entity/arrow.js";
import {
  makeFirebolt, stepFirebolts, COLD_SPEED, type Firebolt,
} from "../entity/firebolt.js";
import { buildingNamed, raise } from "../world/blueprint.js";
import {
  makeBeast, stepBeasts, rollBeastDrop, spook,
  GUARD_REACH, type Beast, type BeastKind, type Bite, type Company,
} from "../entity/beast.js";
import {
  makeFish, stepFish, startle, KARAS_DROP, type Fish,
} from "../entity/fish.js";
import {
  tickVillagers, faceToward, SPEECH_SECONDS, type Villager,
} from "../entity/villager.js";
import {
  explodes, blastCells, blastHurt, highest, CLIMB_SECONDS, GATHER_SECONDS,
  roundAt,
} from "../world/kingHill.js";
import { WarMatch } from "./match.js";
import {
  makeTank, shellStruck, TANK_FLOOR, type Tank,
} from "../entity/tank.js";
import {
  makeShell, stepShells as flyShells, type Shell,
} from "../entity/shell.js";
import {
  BOT_ARMY, BOT_EVERY, BOT_CHOP, FLAG_HEIGHT,
} from "../world/war.js";
import { WAR_GROUND } from "../world/world.js";
import { isFlag, flagTeam } from "../block/registry.js";
import { blowsToBreak } from "../block/breaking.js";
import { BlockId, isLeaf } from "../block/registry.js";
import { DayNightCycle } from "../world/daynight.js";
import { makeBoat, BOAT_DRAFT, type Boat } from "../entity/boat.js";
import {
  strayedFromAll, whatToPutDown, LIFE_KEEP, FOES_KEEP,
  type Wood, type Person,
} from "../entity/restock.js";
import {
  makeOrc as makeFoe, WILD_FOES, nightOnly, dayOnly,
} from "../entity/orc.js";
import { GROW_REACH, FORGET_REACH } from "../world/world.js";
import {
  encodeChunk, encode, type BlockEdit, type ClientMessage, type WorldSeed,
  type CreatureKind, type BagState,
} from "../net/protocol.js";
import { pieces, snapshotObject, snapshotText, type Field } from "../net/snapshot.js";
import {
  loadWorldFrom, saveWorldTo, loadBagsFrom, saveBagsTo, loadNamesFrom,
  saveNamesTo, loadBoatsFrom, saveBoatsTo, loadMetaFrom, saveMetaTo,
  type Keep,
} from "../net/keep.js";
import {
  oneBlockDrop, oneBlockCount, oneBlockBirth, oneBlockWalksOut,
} from "../world/oneBlock.js";
import { spawnForItem } from "../entity/spawn.js";

/**
 * What a realm needs of a person: somewhere to put them, and a way to speak
 * to them. The socket, the budget and the address are the door's business.
 */
export interface RealmClient {
  readonly id: number;
  name: string;
  skinId: string;
  x: number; y: number; z: number;
  yaw: number; pitch: number;
  /** They have said hello and been told the seed. */
  ready: boolean;
  /** In creative, and so not something an orc goes after. */
  flying: boolean;
  drone: { x: number; y: number; z: number; yaw: number } | null;
  /** What is in their hand, and how many blows they have struck. */
  held: number;
  blows: number;
  send(message: object): void;
  /** A message with its text already written. See core/net/snapshot. */
  sendEncoded(message: object, text: string): void;
  sendChunk(frame: ArrayBuffer): void;
  /**
   * Which changed chunks they have already been handed.
   *
   * Per person, because what is streamed to them depends on where they have
   * walked. Cleared when they leave with the rest of them.
   */
  readonly sent: Set<string>;
  /** False once the socket has gone, so a long send can stop early. */
  open(): boolean;
}

/**
 * The most arrows and bolts that may be in the air at once, per list.
 *
 * Each one is a segment march against the blocks every tick. A bow is a
 * button somebody can hold down; two hundred is not somebody playing.
 */
const MAX_ARROWS = 200;
/**
 * How many танки and снаряди a world may hold at once.
 *
 * The same kind of ceiling the boats have and for the same reason: they are
 * made by pressing a button, and a button is a thing somebody can hold down.
 */
const MAX_TANKS = 24;
const MAX_SHELLS = 60;
const MAX_BOLTS = 200;

/**
 * The most човни one world may hold.
 *
 * A boat costs five boards and there is nothing to stop somebody making a
 * hundred of them; each is a box in every snapshot to everybody near it. The
 * ceiling is not a rule about boats, it is a rule about a stranger with a
 * script.
 */
const MAX_BOATS = 120;

/**
 * How far away an animal is still worth telling somebody about.
 *
 * A shade past the client's own draw distance, so one does not pop into
 * existence at the moment it becomes visible - it is already there, being
 * drawn between two snapshots, by the time anybody can see it.
 */
const HERD_SIGHT = 72;

/**
 * How often the hour goes out, in seconds.
 *
 * Rarely, because it is a correction and not a heartbeat: both ends run the
 * same clock at the same speed and the sky moves smoothly between. What this
 * is for is the page that was asleep in a background tab and woke up an hour
 * behind the world it is standing in.
 */
const SKY_EVERY = 15;

/**
 * How far a changed chunk is worth handing over, and how many a tick.
 *
 * Two a tick is thirty a second, which clears the кремль's own chunks in a
 * few seconds and a lived-in world in the time it takes to walk across it -
 * against one burst of everything, which is what it did before.
 *
 * The radius is generous on purpose: a hole in a wall you can see from a
 * hundred blocks away should be there when you look, not when you arrive.
 */
const CHUNK_SIGHT = 160;
const CHUNKS_PER_TICK = 2;

const chunkKey = (cx: number, cy: number, cz: number): string =>
  `${cx},${cy},${cz}`;

/** How far a chunk's middle is from somebody, flat. Height is not a walk. */
function chunkAway(
  chunk: { cx: number; cz: number }, at: { x: number; z: number },
): number {
  const S = CHUNK_SIZE;
  return Math.hypot(chunk.cx * S + S / 2 - at.x, chunk.cz * S + S / 2 - at.z);
}

/** How far a player may reach to change a block. The client's own REACH. */
export const REACH = 6;

export class Realm {
  /** The blocks. Undefined until somebody asks for this world. */
  private grownWorld: World | null = null;
  private dirtyWorld = false;
  private dirtyMeta = false;
  /**
   * Whether this process may write this world down.
   *
   * Off for good if the file that is there could not be read. That mistake -
   * treating an unreadable save as no save, and writing over it half a minute
   * later - is what destroyed a world in the single player game, and it is
   * not being made twice.
   */
  private maySave = true;

  readonly clients = new Map<number, RealmClient>();
  /** Edits made since the last snapshot, batched. */
  pending: BlockEdit[] = [];

  private fire = new Fire();
  private bolts: Firebolt[] = [];
  /**
   * One clock for the whole world.
   *
   * Every page used to run its own, so two people standing in the same field
   * could be at noon and at midnight and neither was wrong. It was a
   * curiosity until something started coming out of the lakes after dark.
   */
  readonly cycle = new DayNightCycle(0.32);
  /** Seconds since the country round the people was last swept. */
  private sweepClock = 0;
  /** And since the hour was last sent out. */
  private skyClock = 0;
  orcs: Orc[] = [];
  /**
   * The livestock, the карасі and the villagers.
   *
   * Here for the same reason the orcs are: everybody had their own fourteen
   * pigs in their own places, and a тур one player shot went on grazing on
   * the other screen. They can hurt nobody, which is why this waited - but
   * two people looking at the same field and seeing different animals in it
   * is the same fault, and it is the one you can see.
   */
  beasts: Beast[] = [];
  fish: Fish[] = [];
  /**
   * The човни, which are furniture and not life.
   *
   * The server owns whether one *exists* and who is in it; where it is comes
   * from whoever is rowing, the same way a player's own position does. That
   * split is the only one that works: a hull with physics on both ends is
   * two things disagreeing about one place, and a hull with physics only on
   * the server is a boat that answers the oars a fifth of a second late.
   */
  boats: Boat[] = [];
  /**
   * The танки, which are the server's list the way the boats are.
   *
   * Not saved with the world: a match is an evening - see server/war.ts -
   * and a machine left standing in a world nobody is playing is a machine
   * with no match around it.
   */
  tanks: Tank[] = [];
  /** Снаряди in the air. The server's are the ones that decide what is hit. */
  private readonly shells: Shell[] = [];
  folk: Villager[] = [];
  private raid = new RaidClock();
  private lairClock = new LairClock();
  /** The orcs' own arrows, and the players'. Never one list: see the tick. */
  private orcArrows: Arrow[] = [];
  playerArrows: Arrow[] = [];
  private lairsLeft = 0;
  private queued: {
    readonly client: RealmClient;
    readonly message: Extract<ClientMessage, { t: "raise" }>;
  }[] = [];

  /** When the last person left, for the clearing-away clock. */
  emptySince = Date.now();
  /** Ticks so far, for what goes out on every other one. */
  private ticks = 0;

  /**
   * When this match ended, if it has.
   *
   * Only a match ever sets it - see `GameMap.match`. The server takes the
   * world down a few seconds later: a race that has been run and a battle
   * that has been won are not places anybody comes back to, and leaving them
   * in the list is what was reported as "бачу король гори в списку".
   */
  finishedAt: number | null = null;

  /**
   * What each person is carrying, against the name they came in under.
   *
   * Loaded with the world and written beside it, so a bag belongs to the
   * world rather than to a browser tab - clearing your history no longer
   * loses your evening, and coming back to a world you left is coming back
   * to what you left it with.
   *
   * Not decided here. See the `bag` message: every act that changes one
   * still happens on the client, so this is their word for what they have.
   */
  private bags = new Map<string, BagState>();
  private bagsDirty = false;
  /** Who owns which name here, and the secret that says so. See store.ts. */
  private names = new Map<string, string>();
  private namesDirty = false;
  private keptRead = false;

  /** Seconds since this round began. See `stepCrown`. */
  private roundClock = 0;
  /** Who is on top of the hill, and for how long. See `stepCrown`. */
  /**
   * The match, on the one map that has one.
   *
   * Null everywhere else, and every branch that touches it says so: a кремль
   * has no teams, no lobby and no flag, and a field of nulls in a class that
   * three maps share is cheaper than a fourth Realm subclass.
   */
  war: WarMatch | null = null;

  private kingId = 0;
  private kingSaid = -1;

  constructor(
    readonly id: string,
    readonly map: GameMap,
    readonly seed: number,
    /**
     * Where this world is written down.
     *
     * An interface, not a directory: on the server it is files, and in a
     * player's browser - which is about to be asked to hold a world of its
     * own - it is whatever that browser can write. Nothing in here knows
     * which, and that is the point.
     */
    readonly keep: Keep,
    /**
     * The word that opens this world.
     *
     * Per world, not per server, because whoever puts a world up is the one
     * who decides who comes into it - and one word for the whole machine
     * meant that letting somebody into a mini-game let them into everything
     * else standing on it.
     */
    readonly word: string,
    /**
     * Who asked for it, if anybody did.
     *
     * A world out of `WORLDS` belongs to the machine and has nobody's name
     * on it. One made through the door belongs to the account that made it,
     * which is how a list can say whose it is and how a cap on how many
     * somebody may have means anything at all.
     */
    readonly owner: string | null = null,
    /**
     * How long the gathering lasts, in seconds.
     *
     * A knob only so the checks can turn it down: a minute of nobody being
     * allowed to move is right for a game and wrong for a script that has to
     * prove the rule in under a second. Never changed in anger.
     */
    readonly gather: number = GATHER_SECONDS,
    /**
     * And how long the climb lasts after it.
     *
     * The same kind of knob, added for the same reason and one the checks
     * needed badly: with the climb fixed at five minutes, *the end of a
     * round* could not be tested at all in under five minutes, so nothing
     * tested it - and the one thing that has now broken twice is what the
     * screen does when a round ends.
     */
    readonly climb: number = CLIMB_SECONDS,
  ) {
    if (map.terrain === "war") this.war = new WarMatch();
  }

  /**
   * Tell everybody where the match has got to.
   *
   * Per client rather than broadcast, because the message carries "you" -
   * which team the person reading it is on - and that is different for
   * everybody who gets it.
   */
  sayWar(): void {
    const war = this.war;
    if (war === null) return;
    for (const c of this.clients.values()) {
      c.send(war.state(c.name, this.hosts(c.name)));
    }
  }

  /**
   * Who runs the match: whoever made the world, or whoever got here first.
   *
   * A world out of `WORLDS` belongs to the machine and has nobody's name on
   * it - so with the owner alone, a match on a world the operator put up
   * could never be started by anybody, which is every world on this server
   * until somebody makes one through the door. First in, and it sticks: the
   * lobby must not change hands because somebody reloaded.
   */
  hosts(name: string): boolean {
    if (this.owner !== null) return name === this.owner;
    this.hostName ??= name;
    return name === this.hostName;
  }

  private hostName: string | null = null;

  /** Grown, or not yet asked for. */
  get isGrown(): boolean { return this.grownWorld !== null; }
  get players(): number {
    let n = 0;
    for (const c of this.clients.values()) if (c.ready) n++;
    return n;
  }

  /**
   * The blocks, growing them if this is the first time anybody asked.
   *
   * Terrain from the seed, then whatever anybody has already built laid over
   * the top - the generator is deterministic, so only the chunks somebody
   * changed are ever on disk.
   */
  get world(): World {
    const already = this.grownWorld;
    if (already !== null) return already;

    const started = Date.now();
    const world = new World({
      seed: this.seed,
      sizeXZ: this.map.sizeXZ,
      sizeY: this.map.sizeY,
      kremlin: this.map.kremlin,
      ...(this.map.terrain !== undefined ? { terrain: this.map.terrain } : {}),
      adult: true,
    });
    world.generate();
    this.grownWorld = world;
    console.log(`${this.id}  grown in ${Date.now() - started} ms, ` +
      `${world.standingLairs().length} lairs`);

    const restored = loadWorldFrom(world, this.keep);
    switch (restored.kind) {
      case "loaded":
        console.log(`${this.id}  restored ${restored.chunks} changed chunks, ` +
          `${world.standingLairs().length} lairs standing`);
        break;
      case "fresh":
        console.log(`${this.id}  nothing saved yet, keeping it in ${this.id}`);
        break;
      case "refused":
        this.maySave = false;
        console.error(`${this.id}  REFUSED: ${restored.why}`);
        console.error(`${this.id}  saving is off for this run. The file on ` +
          "disk is left exactly as it is - move it aside yourself if you " +
          "meant to start again.");
        break;
    }
    this.lairsLeft = world.standingLairs().length;
    this.garrison();
    this.populate();
    for (const kept of loadBoatsFrom(this.keep)) {
      this.boats.push(makeBoat({ x: kept.x, y: kept.y, z: kept.z }, kept.yaw));
    }
    if (this.boats.length > 0) {
      console.log(`${this.id}  ${this.boats.length} човнів still afloat`);
    }
    return world;
  }

  /**
   * The names and the bags, read once and not with the ground.
   *
   * **Deliberately not part of growing the world.** They were, and it was
   * wrong in a way that only showed on a world nobody had opened yet: a name
   * is claimed before the arrival is given a place to stand, and giving them
   * a place is what grows the world - so the claim went into an empty map
   * and then the load put the file over the top of it. The first person to
   * take a name in a fresh world had it taken straight back off them, and
   * the next stranger to type it was let in as them.
   *
   * The checks did not catch it because by the time they got there the world
   * had been grown by something else.
   */
  private ensureKept(): void {
    if (this.keptRead) return;
    this.keptRead = true;
    this.bags = loadBagsFrom(this.keep) as Map<string, BagState>;
    this.names = loadNamesFrom(this.keep);
    if (this.bags.size > 0 || this.names.size > 0) {
      console.log(`${this.id}  ${this.names.size} names, ${this.bags.size} bags kept`);
    }
  }

  /**
   * Is that name theirs to use, and what tag do they keep?
   *
   * Unclaimed: it becomes theirs and they are handed a new tag. Claimed with
   * the right tag: theirs, and the same tag back. Claimed with the wrong one
   * or none: not theirs, and the answer is to pick another name.
   *
   * Somebody already standing in this world under that name is refused even
   * with the right tag. One name is one body: two of you would be two name
   * tags over two backs, both answering to the same crown and the same bag.
   */
  /** Has somebody already taken that name here, account or no account? */
  nameClaimed(name: string): boolean {
    this.ensureKept();
    return this.names.has(name);
  }

  claimName(name: string, token: string | undefined):
    { ok: true; token: string } | { ok: false; why: string } {
    if (name.length === 0) return { ok: false, why: "a name is needed here" };
    this.ensureKept();
    const held = this.names.get(name);
    for (const client of this.clients.values()) {
      if (client.ready && client.name === name) {
        /*
         * Unless it is them, back. A browser closed on a world leaves its
         * connection looking alive for up to half a minute, and the person
         * reopening it was told they were already here - "якщо випадково
         * закрив браузер, немає можливості повернутись". The right tag is
         * proof enough: the one standing there is a ghost, and goes.
         */
        if (held !== undefined && token === held) { this.evict(client); continue; }
        return { ok: false, why: `${name} is already in this world` };
      }
    }
    if (held === undefined) {
      const fresh = newToken();
      this.names.set(name, fresh);
      this.namesDirty = true;
      return { ok: true, token: fresh };
    }
    if (token !== held) return { ok: false, why: `${name} is somebody else's` };
    return { ok: true, token: held };
  }

  /** Does that tag open that name here - or is the name nobody's yet? */
  nameFits(name: string, token: string | undefined): boolean {
    this.ensureKept();
    const held = this.names.get(name);
    return held === undefined || held === token;
  }

  /**
   * The name is theirs: our server vouched for the account. A fresh tag,
   * handed over the way a first claim hands one over; the old tag, lost
   * with a closed private window, opens nothing any more.
   */
  vouchFor(name: string): string {
    this.ensureKept();
    const fresh = newToken();
    this.names.set(name, fresh);
    this.namesDirty = true;
    return fresh;
  }

  /** Somebody standing in for a person who has come back: gone, now. */
  private evict(client: RealmClient): void {
    this.leave(client);
    client.ready = false;
    client.name = "";
  }

  /** What this world is keeping for somebody, if anything. */
  bagFor(name: string): BagState | undefined {
    this.ensureKept();
    return this.bags.get(name);
  }

  /** Their word for what they are carrying. Kept against their name. */
  keepBag(name: string, bag: BagState): void {
    this.ensureKept();
    this.bags.set(name, bag);
    this.bagsDirty = true;
  }

  /**
   * The livestock and the карасі, put out when the world is grown.
   *
   * Not on a hill: «Король гори» is a cube on a floor, with no pasture and no
   * water. Skipped rather than left to find nothing - "find nothing" is four
   * thousand tries at placing a fish in a world with none.
   *
   * The arithmetic is the single-player game's, moved rather than rewritten.
   */
  private populate(): void {
    const world = this.grownWorld;
    /*
     * Nothing lives on the hill, and nothing lives on «Один блок».
     *
     * There is one cell in the whole world there: a herd scattered over it
     * would be fourteen animals falling for ever, and a garrison would be
     * orcs doing the same.
     */
    if (world === null || this.map.terrain === "hill") return;
    if (this.map.terrain === "one") return;
    /*
     * Nor an adventure, which stocks itself as it is walked.
     *
     * There is no island to scatter a herd over and no lake to find that
     * anybody has grown yet: `roam` puts the life down round the people who
     * are actually in the country, a second after the first one arrives.
     */
    if (this.map.terrain === "adventure") return;

    const home = world.islands[0];
    if (home === undefined) return;
    const HERD = 14;
    for (let i = 0; i < HERD; i++) {
      // Two hens to a pig to a тур, which is roughly what a подвір'я holds.
      // The same mix the lone game uses: mostly birds, a few head, and the
      // odd собака or кіт about the place.
      const turn = i % 8;
      const kind: BeastKind =
        turn === 0 ? "tur" : turn === 1 || turn === 5 ? "pig"
        : turn === 3 ? "sobaka" : turn === 7 ? "kit" : "kurka";
      // Scattered over the island rather than penned: they are wild, and
      // finding them is part of hunting them.
      const angle = (i / HERD) * Math.PI * 2 + i * 0.7;
      const away = (0.2 + (i % 5) * 0.14) * home.radius;
      this.beasts.push(makeBeast(kind, world.findSpawn(
        Math.round(home.x + Math.cos(angle) * away),
        Math.round(home.z + Math.sin(angle) * away),
        true,
      )));
    }

    const span = world.opts.sizeXZ * 16;
    const wanted = 40;
    for (let tries = 0; tries < 4000 && this.fish.length < wanted; tries++) {
      const x = 4 + Math.floor(Math.random() * (span - 8));
      const z = 4 + Math.floor(Math.random() * (span - 8));
      const ground = world.groundAt(x, z);
      // Two clear cells of water above the bed, so it has somewhere to swim
      // and is not spawned wedged against the bottom.
      if (ground >= world.seaLevel - 1) continue;
      const y = ground + 2;
      if (!this.fluidAt(x, y, z) || !this.fluidAt(x, y + 1, z)) continue;
      this.fish.push(makeFish({ x: x + 0.5, y: y + 0.5, z: z + 0.5 }));
    }
    console.log(`${this.id}  ${this.beasts.length} beasts, ${this.fish.length} fish`);
  }

  private fluidAt = (x: number, y: number, z: number): boolean =>
    isWater(this.world.getBlock(x, y, z));

  /** What a client needs to grow the same terrain. */
  worldSeed(): WorldSeed {
    const world = this.world;
    return {
      seed: world.opts.seed, sizeXZ: world.opts.sizeXZ,
      sizeY: world.opts.sizeY, kremlin: this.map.kremlin,
      ...(this.map.terrain !== undefined ? { terrain: this.map.terrain } : {}),
    };
  }

  solidAt = (x: number, y: number, z: number): boolean =>
    isSolid(this.world.getBlock(x, y, z));

  /** Something changed that is worth writing down. */
  touch(): void { this.dirtyWorld = true; }

  /**
   * Two guards at every lair, and one leader over the lot.
   *
   * The garrison is what makes walking into the кремль mean walking into
   * somebody. Put out when the world is grown rather than on the first
   * arrival: a fortress that fills only when somebody is watching is a
   * fortress the second player finds already emptied.
   */
  private garrison(): void {
    const world = this.grownWorld;
    if (world === null) return;
    const lairs = world.standingLairs();
    for (const lair of lairs) {
      for (let i = 0; i < 2; i++) {
        this.orcs.push(makeOrc(
          world.findSpawn(lair.x + (i === 0 ? 3 : -3), lair.z + 2),
          i === 0 ? "sabre" : "archer",
          lair,
        ));
      }
    }
    const first = lairs[0];
    if (first !== undefined && !this.orcs.some((o) => o.breed.kind === "leader")) {
      this.orcs.push(makeOrc(world.findSpawn(first.x, first.z + 4), "leader"));
    }

    /*
     * And the замок, if this world has one.
     *
     * The same two-to-a-room garrison the single-player game puts down, here
     * as well: a castle that is full of guards alone and empty together is
     * two different games wearing one name.
     */
    const castle = world.castleAt;
    if (castle === null) return;
    for (const post of CASTLE_POSTS) {
      const cell = castleCell(castle, post);
      this.orcs.push(makeOrc(
        { x: cell.x + 0.5, y: cell.y, z: cell.z + 0.5 }, "skeleton"));
    }
    const seat = castleCell(castle, CASTLE_THRONE);
    this.orcs.push(makeOrc(
      { x: seat.x + 0.5, y: seat.y, z: seat.z + 0.5 }, "chakhlyk"));
  }

  /**
   * Put a wave on the shore of the players' island.
   *
   * Spread along the shore so one wall cannot hold the whole wave, and one
   * archer in every three.
   */
  private landRaid(count: number): void {
    const world = this.world;
    const near = world.islands[0];
    const far = world.islands[1];
    if (near === undefined || far === undefined) return;
    for (let i = 0; i < count; i++) {
      const toward = Math.atan2(far.z - near.z, far.x - near.x);
      const angle = toward + (i / Math.max(1, count - 1) - 0.5) * 0.9;
      const away = near.radius * 0.92;
      this.orcs.push(makeOrc(world.findSpawn(
        Math.round(near.x + Math.cos(angle) * away),
        Math.round(near.z + Math.sin(angle) * away),
      ), i % 3 === 2 ? "archer" : "sabre"));
    }
  }

  /** Where a new arrival is put: the middle of the players' island. */
  spawnPoint(): { x: number; y: number; z: number } {
    const world = this.world;
    /*
     * On «Один блок» the spawn is the block, for everybody, always.
     *
     * Not a shortcut: it is the map's rule - "it is the spawn, it is where
     * anybody who dies comes back to". And the search below, which walks
     * the ground round the start looking for a square nobody is standing
     * on, has nothing to find here: every one of its twenty-five tries
     * crawled two hundred blocks of void to the same one block and found
     * it taken. Six hundred milliseconds a join on a quiet machine; past
     * five seconds under the wire suite, where the door then timed out.
     */
    const block = world.oneBlockAt;
    if (block !== null) return { x: block.x + 0.5, y: block.y + 1, z: block.z + 0.5 };
    const home = world.islands[0]!;
    const hx = Math.round(home.x);
    const hz = Math.round(home.z);
    /*
     * Beside the others, not inside them.
     *
     * `findSpawn` walks outward from the place it is given and returns the
     * first spot that will hold somebody - which for two people given the
     * same place is the same spot, and two козаки standing in one block look
     * like one козак with a flicker. So each arrival is offered the ground
     * round the start in turn and takes the first square nobody is standing
     * on.
     *
     * Rings rather than a random scatter: on a hill the start is a platform
     * a few blocks wide, and a scatter would put somebody off the edge of it.
     */
    const here: { x: number; z: number }[] = [];
    for (const client of this.clients.values()) {
      if (client.ready) here.push({ x: client.x, z: client.z });
    }
    for (let ring = 0; ring <= 4; ring++) {
      for (let dz = -ring; dz <= ring; dz++) {
        for (let dx = -ring; dx <= ring; dx++) {
          if (ring > 0 && Math.abs(dx) !== ring && Math.abs(dz) !== ring) continue;
          const at = world.findSpawn(hx + dx * 2, hz + dz * 2);
          if (here.every((who) => Math.hypot(who.x - at.x, who.z - at.z) > 1.4)) {
            return at;
          }
        }
      }
    }
    // Everywhere within nine blocks is taken, which is a crowd. Put them at
    // the start and let them sort it out.
    return world.findSpawn(hx, hz);
  }

  join(client: RealmClient): void {
    this.clients.set(client.id, client);
    // A minute that started while the machine was booting is a minute nobody
    // was there for: the first person through the door is who it counts from.
    this.maybeRestartRound();
  }

  leave(client: RealmClient): void {
    // Whatever they were in is empty now: a boat with a rider who is not on
    // the server is a boat nobody can ever get into again.
    this.ashore(client.id);
    this.clients.delete(client.id);
    if (this.clients.size === 0) this.emptySince = Date.now();
    if (client.ready) this.broadcast({ t: "bye", id: client.id });
  }

  send(client: RealmClient, message: object): void { client.send(message); }

  broadcast(message: object, except?: number): void {
    // Written once, however many are listening.
    let text: string | null = null;
    for (const client of this.clients.values()) {
      if (client.id === except) continue;
      if (!client.ready) continue;
      text ??= encode(message as never);
      client.sendEncoded(message, text);
    }
  }

  /** Whoever that id belongs to in this world, if they are still here. */
  /** Everybody who is in, for the few things that walk the room. */
  everybody(): readonly RealmClient[] {
    return [...this.clients.values()].filter((c) => c.ready);
  }

  clientById(id: number): RealmClient | undefined {
    const found = this.clients.get(id);
    return found !== undefined && found.ready ? found : undefined;
  }

  /**
   * Everything the world has that the seed does not, a little at a time.
   *
   * A client grows the terrain itself from the seed - the generator is
   * deterministic, so the two agree block for block - and is sent only the
   * chunks somebody has changed. Those used to go **all at once, at the
   * door**: fine for one fortress on a fresh world, and a megabyte in a
   * single breath on a world people have lived in for a month, before the
   * new arrival had drawn a frame.
   *
   * So they are streamed instead: the nearest few each tick, out to a radius,
   * and never one they already have. Somebody walking east is handed the east
   * as they go.
   *
   * **Edits are not filtered this way and must not be.** A block somebody
   * changes travels to everybody, near or far, because a client that missed
   * one and already holds that chunk would be wrong about it for ever. A
   * chunk that arrives afterwards is the server's whole current copy and
   * includes every edit up to that moment, so the two orders both come out
   * right.
   */
  private streamChunks(client: RealmClient, changed: readonly Chunk[]): void {
    if (changed.length === 0 || client.sent.size >= changed.length) return;
    let budget = CHUNKS_PER_TICK;
    /*
     * Nearest first, so what is under their feet arrives before what is over
     * the hill. Sorted rather than taken in whatever order the map yields:
     * the first version handed out chunk (0,0,0) first to somebody standing
     * in the far corner, and they watched their own hole appear last.
     */
    const near = changed
      .filter((c) => !client.sent.has(chunkKey(c.cx, c.cy, c.cz)))
      .map((c) => ({ c, away: chunkAway(c, client) }))
      .filter((e) => e.away <= CHUNK_SIGHT)
      .sort((p, q) => p.away - q.away);

    for (const { c } of near) {
      if (budget-- <= 0) return;
      if (!client.open()) return;
      client.sendChunk(encodeChunk(c.cx, c.cy, c.cz, c.blocks));
      client.sent.add(chunkKey(c.cx, c.cy, c.cz));
    }
  }

  /** A building, queued rather than raised here. Says whether it fitted in. */
  queueRaise(
    client: RealmClient, message: Extract<ClientMessage, { t: "raise" }>,
    max: number,
  ): boolean {
    if (this.queued.filter((q) => q.client === client).length >= max) return false;
    this.queued.push({ client, message });
    return true;
  }

  private raiseNext(): void {
    const next = this.queued.shift();
    if (next === undefined) return;
    const { client, message } = next;
    // They may have left while it waited.
    if (!this.clients.has(client.id)) return;
    const building = buildingNamed(message.building);
    if (building === undefined) return;
    const world = this.world;
    const facing = (message.facing & 3) as 0 | 1 | 2 | 3;
    const cells: BlockEdit[] = [];
    const verdict = raise(
      building, facing, message.x, message.y, message.z,
      (bx, by, bz) => world.getBlock(bx, by, bz),
      (bx, by, bz) => world.withinBounds(bx, by, bz),
      (bx, by, bz, blockId) => { cells.push({ x: bx, y: by, z: bz, id: blockId }); },
      isNaturalGround,
      world.seaLevel + 1,
    );
    if (verdict !== "ok") {
      /*
       * `build`, not a bare refusal. A refusal about a thing you asked for is
       * not a refusal of you, and the client used to read every `no` as the
       * connection failing.
       */
      client.send({ t: "no", code: "build", why: verdict });
      return;
    }
    world.setBlocks(cells);
    this.dirtyWorld = true;
    this.broadcast({
      t: "raised", building: building.name, facing,
      x: message.x, y: message.y, z: message.z,
    });
  }

  /**
   * Break a block, and let it go off if it is one of the live ones.
   *
   * @returns the cells that changed, so the caller can push them at everybody.
   *
   * **A blast does not set off what it takes.** Three blocks in ten are live,
   * so a chain reaction is self-sustaining by a wide margin: the first
   * unlucky block would take the whole hundred-block cube with it and the map
   * would be over in one click. What a blast removes is simply removed.
   *
   * The floor is left alone for the same reason it is stone: something has to
   * be safe to stand on, and a hole in the bottom of the world is a way out
   * of it.
   */
  /**
   * How many times this world's шопопало-блок has been hit open.
   *
   * The server's, because the block is the world's: everybody standing at it
   * has to be walking down the same table, and a page that kept its own
   * count would be handed a different game from the person beside it. Saved
   * with the world, so a server that restarts does not start the table over.
   */
  private breaksRead = false;
  private breaks = 0;

  /**
   * Read off disk the first time it is asked for.
   *
   * A field initialiser runs before the constructor's parameters exist, so
   * `this.dir` is not there yet - and a count read from the wrong directory
   * is a count read from nowhere.
   */
  get oneBlockBreaks(): number {
    if (!this.breaksRead) {
      this.breaks = loadMetaFrom(this.keep).oneBlockBreaks ?? 0;
      this.breaksRead = true;
    }
    return this.breaks;
  }

  set oneBlockBreaks(n: number) {
    this.breaksRead = true;
    this.breaks = n;
  }

  /**
   * Open the шопопало-блок once, on behalf of whoever hit it.
   *
   * A creature is put into the world here and reaches everybody in the next
   * snapshot - "хочу щоб істоти вискакували одразу". Anything you can carry
   * comes back for the server to hand to that one person, because a bag is
   * theirs and nobody else's.
   */
  openOneBlock(): { item: number; count: number } | null {
    const world = this.grownWorld;
    const at = world?.oneBlockAt ?? null;
    if (world === null || at === null) return null;

    const item = oneBlockDrop(this.seed, ++this.oneBlockBreaks);
    this.dirtyMeta = true;

    // Whether it walks out at all is one rule, kept with the block, so
    // the check that counts what was handed over walks the same one.
    if (!oneBlockWalksOut(item)) return { item, count: oneBlockCount(item) };

    const def = spawnForItem(item);
    const born = oneBlockBirth(at);
    switch (def?.kind) {
      case "pig": case "tur": case "kurka": case "sobaka": case "kit":
        this.beasts.push(makeBeast(def.kind, born));
        break;
      case "orc":
        this.orcs.push(makeOrc(born));
        break;
      default:
        // A walker the switch does not know how to stand up: hand it over
        // rather than lose it, and let the node suite say so.
        return { item, count: oneBlockCount(item) };
    }
    return null;
  }

  breakBlock(x: number, y: number, z: number, held = 0): BlockEdit[] {
    const world = this.world;
    const was = world.getBlock(x, y, z);

    /*
     * The two refusals the server makes for itself.
     *
     * Everything else about digging is the page's - what it is holding, how
     * long it held the button - and that is the same trust `place` already
     * has. These two are not: a незламний блок nobody can break is the floor
     * of the world and the wall of a match, and "прапор можна зламати тільки
     * кайлом з адамантію" is how a side wins. A page that lied about either
     * would not be playing the game everybody else is.
     */
    if (blowsToBreak(was, held) === 0) return [];
    if (isFlag(was)) {
      const war = this.war;
      const team = flagTeam(was);
      if (war === null || !war.running) return [];
      war.flagDown(team);
      // Everybody is told at once: whose flag fell, and - if that was the
      // last but one - that the match is over and here is the table.
      this.sayWar();
      // And a battle that is over is a world with nothing left in it: the
      // server takes it down once everybody has read the table.
      if (war.over) this.finishedAt ??= Date.now();
    }
    /*
     * The шопопало-блок stays where it is.
     *
     * On «Один блок» that cell is the floor, the spawn and the whole of the
     * map's supply, so a break takes nothing away: four blows shake
     * something loose into the digger's own bag - the page works out what,
     * from the seed, with `oneBlockDrop` - and the world is unchanged. Which
     * also means there is nothing to send: no edit, no water to settle, and
     * no way for one person's digging to drop everybody else into the dark.
     */
    if (was === BlockId.Random) return [];

    const edits: BlockEdit[] = [{ x, y, z, id: BlockId.Air }];
    world.setBlock(x, y, z, BlockId.Air);
    /*
     * And whatever was standing next to the hole finds its level.
     *
     * The server never did this: water was worked out only on the page that
     * dug, so the person digging watched a trench fill and nobody else saw a
     * drop of it. The cells arrive on the clock and go out as ordinary edits
     * with everything else.
     */
    world.updateWater(x, y, z);
    world.updateLava(x, y, z);
    world.updateOil(x, y, z);

    /*
     * And a смолоскип hung on the wall that has just gone.
     *
     * The page that dug does this for itself; without it here the server
     * kept the torch, and its next word about that chunk put it back - a
     * torch hanging in the air that comes back every time you break it.
     * The facing names the wall, so the one to take down is the neighbour
     * looking back at this block.
     */
    for (const [dx, dz, back] of
      [[1, 0, 3], [-1, 0, 1], [0, 1, 0], [0, -1, 2]] as const) {
      const beside = world.getBlock(x + dx, y, z + dz);
      if (!isWallTorch(beside) || wallTorchFacing(beside) !== back) continue;
      world.setBlock(x + dx, y, z + dz, BlockId.Air);
      edits.push({ x: x + dx, y, z: z + dz, id: BlockId.Air });
    }

    if (was !== BlockId.Boom || !explodes(x, y, z, this.seed)) {
      this.dirtyWorld = true;
      this.pending.push(...edits);
      return edits;
    }

    for (const cell of blastCells(x, y, z)) {
      if (!world.withinBounds(cell.x, cell.y, cell.z)) continue;
      // Only the hill. The stone floor under it stays.
      if (world.getBlock(cell.x, cell.y, cell.z) !== BlockId.Boom) continue;
      world.setBlock(cell.x, cell.y, cell.z, BlockId.Air);
      edits.push({ x: cell.x, y: cell.y, z: cell.z, id: BlockId.Air });
    }
    this.dirtyWorld = true;
    this.pending.push(...edits);

    // Everybody near enough to feel it, which includes whoever set it off.
    for (const client of this.clients.values()) {
      if (!client.ready) continue;
      const away = Math.hypot(client.x - x, client.y - y, client.z - z);
      const hurt = blastHurt(away);
      if (hurt > 0) client.send({ t: "hurt", amount: hurt });
    }
    // And everybody at all, so it is heard rather than only felt.
    this.broadcast({ t: "boom", x, y, z });
    return edits;
  }

  /**
   * Who is on top, and for how long.
   *
   * Only spoken when it changes hands or the count passes another second: it
   * is a line of text, not a position, and fifteen a second of the same
   * sentence is fourteen wasted.
   */
  private stepCrown(dt: number): void {
    const standing = [...this.clients.values()].filter((c) => c.ready);
    const king = highest(standing);
    const id = king?.id ?? 0;

    /*
     * One clock for the whole round, and the phase read off it.
     *
     * A minute for everybody to arrive, in which nobody may move, and then
     * five to climb. Whoever is highest when those five run out has won, and
     * the next round starts at once.
     */
    const before = roundAt(this.roundClock, this.gather, this.climb);
    this.roundClock += dt;
    const now = roundAt(this.roundClock, this.gather, this.climb);
    const ended = this.roundClock >= this.gather + this.climb;

    if (ended) {
      /*
       * Won, and then it starts again.
       *
       * Nothing resets the world - the hill they climbed is the hill they
       * are standing on, and knocking it down to start over would throw away
       * the one thing everybody just spent five minutes making.
       */
      /*
       * The round is over, and that is what is sent - not "somebody won".
       *
       * It used to say `won: id !== 0`, and the page puts its end-of-round
       * screen up off that flag. So a round that ended with nobody on the
       * hill - or with two people standing at exactly the same height, which
       * `highest` refuses to choose between - told everybody nothing, and
       * the next round simply began under them: the mouse still captured,
       * the two buttons never drawn, and no way to tell a restart from a
       * hang. The single-player hill never had this, because it calls the
       * same screen with an empty name and lets it say «Ніхто не дійшов».
       */
      this.broadcast({
        t: "king", id, name: king?.name ?? "", held: 0, over: true,
        phase: "gather", left: this.gather,
      });
      /*
       * And that is the end of it, rather than the start of another.
       *
       * The round used to begin again under everybody, which is what the
       * hill was before anybody played it with other people: one world, up
       * for ever, racing itself. A match is a match - the server takes this
       * world down in a moment, and whoever wants another makes one.
       */
      this.finishedAt ??= Date.now();
      this.roundClock = 0;
      this.kingId = id;
      this.kingSaid = -1;
      return;
    }

    // A second has turned over, or the round has changed halves: both are
    // things every screen has to be told about, and nothing else is.
    const whole = Math.floor(now.left);
    if (whole === this.kingSaid && id === this.kingId &&
      before.phase === now.phase) return;
    this.kingSaid = whole;
    this.kingId = id;
    this.broadcast({
      t: "king", id, name: king?.name ?? "", held: 0, over: false,
      phase: now.phase, left: now.left,
    });
  }

  /**
   * The match's clock, and the moment the wall becomes wood.
   *
   * "Коли час на підготовку закінчився стіна перетворюється на блок дерева."
   * The turn is thousands of ordinary block edits, so it goes out the way
   * every other edit does - through `pending`, on the clock - and a page
   * that was not looking at the wall hears about it when it gets there.
   */
  private stepWar(dt: number): void {
    const war = this.war;
    if (war === null || this.grownWorld === null) return;
    const turned = war.tick(dt);
    // The time ran out: over, told, and taken down like any finished match.
    if (war.over && this.finishedAt === null) {
      this.finishedAt = Date.now();
      this.sayWar();
      return;
    }
    if (turned === "opened") {
      const world = this.grownWorld;
      const before = new Map<string, number>();
      // What the wall was, so only the cells that actually changed are sent.
      world.openWarEach(war.teams.length, (x, y, z, id) => {
        this.pending.push({ x, y, z, id });
        before.set(`${x},${y},${z}`, id);
      });
      this.touch();
      this.sayWar();
      return;
    }
    // A second has turned over: the clock on everybody's screen is this one.
    const whole = Math.floor(war.left);
    if (whole === this.warSaid) return;
    this.warSaid = whole;
    this.sayWar();
  }

  private warSaid = -1;

  /**
   * The bot team: keep it up to strength, and point it at the flag.
   *
   * @returns where the orcs walk when they can see nobody, or null.
   */
  private stepBots(dt: number): { x: number; z: number } | null {
    const war = this.war;
    const world = this.grownWorld;
    if (war === null || world === null || !war.bots) return null;
    if (!war.running) return null;

    const bots = war.teams.find((t) => t.bots);
    const foes = war.teams.filter((t) => !t.bots && t.alive);
    if (bots === undefined || !bots.alive || foes.length === 0) return null;
    const theirFlag = war.flagAt(foes[0]!.id, world);

    /*
     * Not during the preparing: "гравці можуть підготуватись" is the whole
     * point of those ten minutes, and an army arriving inside them is not
     * preparation, it is the match starting early.
     */
    if (!war.fighting) return null;

    // Kept topped up, and born at home.
    this.botSpawn -= dt;
    if (this.botSpawn <= 0 && this.orcs.length < BOT_ARMY) {
      this.botSpawn = BOT_EVERY;
      const home = war.flagAt(bots.id, world);
      const at = world.findSpawn(
        home.x + (this.orcs.length % 7) - 3,
        home.z + (this.orcs.length % 5) - 2,
      );
      // Two sappers always, to cut the way; the rest as before.
      const sappers = this.orcs.filter((o) => o.breed.kind === "sapper").length;
      this.orcs.push(makeOrc(at, sappers < 2 ? "sapper"
        : this.orcs.length % 3 === 2 ? "archer" : "sabre"));
    }

    /*
     * And the flag comes down if they stand on it long enough.
     *
     * An orc has no адамантій and never will - it is a rule about players -
     * so a bot team could never win by the rule everybody else plays by.
     * Standing over the enemy flag is the bots' version of digging at it,
     * and it is deliberately slow: half a minute of nobody at home.
     */
    let chopping = false;
    for (const orc of this.orcs) {
      if (Math.hypot(orc.x - theirFlag.x - 0.5, orc.z - theirFlag.z - 0.5) > 2.5) {
        continue;
      }
      chopping = true;
      break;
    }
    if (chopping) {
      this.botChop += dt;
      if (this.botChop >= BOT_CHOP) {
        this.botChop = 0;
        const cell = {
          x: theirFlag.x, y: WAR_GROUND + FLAG_HEIGHT + 1, z: theirFlag.z,
        };
        const flag = world.getBlock(cell.x, cell.y, cell.z);
        if (isFlag(flag)) {
          world.setBlock(cell.x, cell.y, cell.z, BlockId.Air);
          this.pending.push({ ...cell, id: BlockId.Air });
          war.flagDown(flagTeam(flag));
          this.sayWar();
          // The bots' way of winning ends the battle the same as anybody
          // else's: the world goes once the table has been read.
          if (war.over) this.finishedAt ??= Date.now();
        }
      }
    } else {
      this.botChop = 0;
    }
    return theirFlag;
  }

  private botSpawn = 0;

  /**
   * A sapper's кайло on this world: whatever a player's iron кайло breaks
   * comes out, sent to everybody like any other edit. Nothing незламне, no
   * прапор, and not the air or the water.
   */
  private sapperDig = (x: number, y: number, z: number): boolean => {
    const world = this.grownWorld;
    if (world === null) return false;
    const id = world.getBlock(x, y, z);
    if (id === BlockId.Air || isWater(id)) return false;
    if (blowsToBreak(id, PlainItemId.IronPickaxe) <= 0) return false;
    world.setBlock(x, y, z, BlockId.Air);
    this.pending.push({ x, y, z, id: BlockId.Air });
    this.touch();
    return true;
  };
  private botChop = 0;

  /** Is anybody allowed to move? Only the hill ever says no. */
  get frozen(): boolean {
    if (this.map.terrain !== "hill") return false;
    return roundAt(this.roundClock, this.gather, this.climb).frozen;
  }

  /**
   * Start the round over, because somebody has arrived at an empty world.
   *
   * The minute is for everybody to gather, and a minute that started while
   * the machine was booting is a minute nobody was there for - so the first
   * person through the door is who it is measured from. Anybody after them
   * joins the round in progress, which is the same deal every game of this
   * shape offers: you wait for the next one.
   */
  private maybeRestartRound(): void {
    if (this.map.terrain !== "hill") return;
    /*
     * Only into an empty world, and `players` counts the ones who are ready.
     *
     * It said `> 1`, and the client that is joining is not ready yet - so
     * with one person already running, `players` was one and the round
     * started over under them. Which is what was reported: a second player
     * arrived and the whole race began again.
     */
    if (this.players > 0) return;
    this.roundClock = 0;
    this.kingSaid = -1;
  }

  /**
   * Is the door still open on this world?
   *
   * Only a hill has a door that shuts. It is a five-minute race with a
   * minute at the start for everybody to gather, and somebody arriving after
   * that either restarts the race for the people already running it or joins
   * one they cannot win. Both happened.
   */
  get openToJoiners(): boolean {
    if (this.map.terrain !== "hill") return true;
    if (this.players === 0) return true;
    return roundAt(this.roundClock, this.gather, this.climb).phase === "gather";
  }

  /**
   * A blow at something alive, from somebody standing at `from`.
   *
   * @returns whether it landed, so the caller can refuse one thrown from
   * across the water without knowing what a pig is.
   *
   * Each of them answers a blow in its own way, which is the reason this is
   * one function and not four call sites: an animal runs from wherever the
   * blow came from, a villager turns round and says his piece, and an orc
   * simply takes it.
   */
  strike(
    kind: CreatureKind, id: number, damage: number,
    from: { x: number; y: number; z: number }, reach: number,
    /** Who is swinging, so nobody can hit themselves to no purpose. */
    by = 0,
  ): boolean {
    /*
     * A person is not on any of those lists, and takes their blow by post.
     *
     * Their сила is their own page's - the same split every other body in
     * this game does *not* have, and the right one here: armour, falling and
     * drowning are all theirs already, and a health bar with two owners is a
     * health bar that flickers. So the server checks that the swing could
     * have reached and tells them it landed.
     */
    if (kind === "player") {
      if (id === by) return false;
      const them = this.clients.get(id);
      if (them === undefined || !them.ready) return false;
      if (Math.hypot(them.x - from.x, them.y - from.y, them.z - from.z)
        > reach) return false;
      // Who struck it travels with the blow: the page that takes it is the
      // only one that knows whether it was the last, and a table of kills
      // needs to know whose it was. See the `died` message.
      them.send({ t: "hurt", amount: damage, from: "player", by });
      return true;
    }
    const list: readonly { id: number; x: number; y: number; z: number }[] =
      kind === "orc" ? this.orcs
      : kind === "beast" ? this.beasts
      : kind === "fish" ? this.fish
      : this.folk;
    const target = list.find((c) => c.id === id);
    if (target === undefined) return false;
    if (Math.hypot(target.x - from.x, target.y - from.y, target.z - from.z)
      > reach) return false;

    const beast = this.beasts.find((b) => b.id === id && kind === "beast");
    const fish = this.fish.find((f) => f.id === id && kind === "fish");
    const folk = this.folk.find((v) => v.id === id && kind === "villager");
    const orc = this.orcs.find((o) => o.id === id && kind === "orc");
    const dealt = (beast ?? fish ?? folk ?? orc)?.vitals.hurt(damage) ?? 0;
    // A blow that lands during the grace period does nothing, and must not
    // set anybody off either - one swing would otherwise spook three times.
    if (dealt <= 0) return true;
    if (beast !== undefined) spook(beast, from.x, from.z);
    if (fish !== undefined) startle(fish, from.x, from.z);
    if (folk !== undefined) {
      faceToward(folk, from.x, from.z);
      folk.speaking = SPEECH_SECONDS;
      folk.said++;
    }
    return true;
  }

  /**
   * Everybody a dog might walk beside, and what is on them.
   *
   * The foe is simply the nearest орк within a few blocks: a dog that goes
   * for whatever is closest to its person needs no memory of who started it
   * and behaves the way a dog behaves. Anything hostile added later belongs
   * in this one list.
   */
  private company(): Company[] {
    const out: Company[] = [];
    for (const client of this.clients.values()) {
      if (!client.ready) continue;
      let foe: Company["foe"] = null;
      let near = GUARD_REACH;
      for (const orc of this.orcs) {
        const away = Math.hypot(orc.x - client.x, orc.z - client.z);
        if (away < near) { near = away; foe = { id: orc.id, x: orc.x, y: orc.y, z: orc.z }; }
      }
      out.push({ id: client.id, x: client.x, y: client.y, z: client.z, foe });
    }
    return out;
  }

  /** A dog got its teeth in. The bite lands on the server's own orc. */
  private biteFor(bite: Bite): void {
    const orc = this.orcs.find((o) => o.id === bite.foe);
    if (orc === undefined) return;
    if (orc.vitals.hurt(bite.damage) > 0) {
      this.broadcast({ t: "strike", kind: "orc", id: orc.id, damage: bite.damage });
    }
  }

  /**
   * The top of the water in a column, for a boat to float on.
   *
   * Walked down from well above the ground rather than up from it, for the
   * reason main.ts's copy is: `groundAt` over a lake is the *bed*, so a
   * search upward from it would have to know how deep the lake is before it
   * could find the top.
   */
  private surfaceAt(x: number, z: number): number | null {
    const col = Math.floor(x);
    const row = Math.floor(z);
    const bed = this.world.groundAt(col, row);
    if (bed < 0) return null;
    for (let y = bed + 24; y > bed; y--) {
      if (isWater(this.world.getBlock(col, y, row))) return y + 1;
    }
    return null;
  }

  /** Put one on the water, if there is water there. */
  launch(x: number, z: number, yaw: number): Boat | null {
    if (this.boats.length >= MAX_BOATS) return null;
    const top = this.surfaceAt(x + 0.5, z + 0.5);
    if (top === null) return null;
    const boat = makeBoat(
      { x: x + 0.5, y: top + BOAT_DRAFT, z: z + 0.5 }, yaw);
    this.boats.push(boat);
    return boat;
  }

  /** Whichever boat this person is in, if any. */
  boatOf(who: number): Boat | undefined {
    return this.boats.find((b) => b.rider === who);
  }

  /**
   * Somebody gets in. Refused if it is taken, or if it is out of reach.
   *
   * Reach rather than nothing, because "I am in that boat" is a claim about
   * a place, and a claim about a place three hundred blocks away is the one
   * kind of lie a server can always catch.
   */
  board(who: number, id: number, from: { x: number; y: number; z: number },
    reach: number): boolean {
    const boat = this.boats.find((b) => b.id === id);
    if (boat === undefined || boat.rider !== null) return false;
    if (Math.hypot(boat.x - from.x, boat.y - from.y, boat.z - from.z) > reach) {
      return false;
    }
    // Out of whatever else they were in, so one person is never in two.
    const was = this.boatOf(who);
    if (was !== undefined) was.rider = null;
    boat.rider = who;
    return true;
  }

  /** And gets out. */
  ashore(who: number): void {
    const boat = this.boatOf(who);
    if (boat !== undefined) boat.rider = null;
  }

  /**
   * And picks one up, which is the only way a boat ever leaves the water.
   *
   * Not one with somebody in it: a passenger cannot pocket the boat they are
   * standing in, and neither can anybody else pocket it out from under them.
   */
  scuttle(who: number, id: number, from: { x: number; y: number; z: number },
    reach: number): boolean {
    const at = this.boats.findIndex((b) => b.id === id);
    if (at < 0) return false;
    const boat = this.boats[at]!;
    if (boat.rider !== null) return false;
    if (Math.hypot(boat.x - from.x, boat.y - from.y, boat.z - from.z) > reach) {
      return false;
    }
    void who;
    this.boats.splice(at, 1);
    return true;
  }

  /*
   * The танк: the same five verbs a човен has, and the gun.
   *
   * What the server owns here is what it owns about a boat - who is in it
   * and whether a claim about a place is plausible - plus the one thing a
   * boat has no equivalent of: how many shells a machine has taken. That
   * count decides when a tank goes up and it cannot live on a page, or two
   * pages would disagree about the fifth hit.
   */
  deploy(at: { x: number; y: number; z: number }, yaw: number): Tank | null {
    if (this.map.terrain !== "war") return null;
    if (this.tanks.length >= MAX_TANKS) return null;
    const tank = makeTank(at, yaw);
    this.tanks.push(tank);
    return tank;
  }

  /** Whichever танк this person is driving, if any. */
  tankOf(who: number): Tank | undefined {
    return this.tanks.find((t) => t.rider === who);
  }

  mount(who: number, id: number, from: { x: number; y: number; z: number },
    reach: number): boolean {
    const tank = this.tanks.find((t) => t.id === id);
    if (tank === undefined || tank.rider !== null) return false;
    if (Math.hypot(tank.x - from.x, tank.y - from.y, tank.z - from.z) > reach) {
      return false;
    }
    const was = this.tankOf(who);
    if (was !== undefined) was.rider = null;
    tank.rider = who;
    return true;
  }

  dismount(who: number): void {
    const tank = this.tankOf(who);
    if (tank !== undefined) tank.rider = null;
  }

  /** The hull's heading, which is all that travels while somebody drives. */
  steer(who: number, yaw: number): void {
    const tank = this.tankOf(who);
    if (tank !== undefined) tank.yaw = yaw;
  }

  /**
   * A shell somebody fired.
   *
   * Passed on to everybody at once so that every page draws it crossing the
   * field, and flown here as well - the flight here is the one that decides
   * what it hit.
   */
  fireShell(
    from: { x: number; y: number; z: number },
    velocity: { x: number; y: number; z: number },
    tankId: number,
  ): boolean {
    if (this.shells.length >= MAX_SHELLS) return false;
    const tank = this.tanks.find((t) => t.id === tankId);
    if (tank === undefined) return false;
    this.shells.push(makeShell(from, velocity, tankId));
    this.broadcast({
      t: "shell", x: from.x, y: from.y, z: from.z,
      vx: velocity.x, vy: velocity.y, vz: velocity.z, from: tankId,
    });
    return true;
  }

  /**
   * Fly them, and count what they hit.
   *
   * "Танк можна розбити тільки пострілами з іншого танку за 5 попадань.
   * Потім танк вибухає." The fifth takes the machine out of the world and
   * goes out as a `boom` with the tank flag on it - every page then works
   * out for itself what that did to the person reading it, which is the
   * arrangement every other kind of damage already has.
   */
  private stepShells(dt: number): void {
    if (this.shells.length === 0) return;
    const solid = (x: number, y: number, z: number): boolean =>
      isSolid(this.world.getBlock(x, y, z));
    for (const hit of flyShells(this.shells, dt, solid, this.tanks)) {
      if (hit.tank === null) continue;
      if (!shellStruck(hit.tank)) continue;
      const gone = this.tanks.indexOf(hit.tank);
      if (gone >= 0) this.tanks.splice(gone, 1);
      this.broadcast({
        t: "boom", x: hit.tank.x, y: hit.tank.y, z: hit.tank.z, tank: true,
      });
    }
  }

  /** An arrow somebody loosed, if there is room in the air for it. */
  shoot(from: { x: number; y: number; z: number },
    dir: { x: number; y: number; z: number }): boolean {
    if (this.playerArrows.length >= MAX_ARROWS) return false;
    this.playerArrows.push(makeArrow(from, dir));
    return true;
  }

  /**
   * A spark struck into a cell. True if it took.
   *
   * Not fierce: a кресало lights straw and boards, and no amount of striking
   * will light a brick. That is `catchesFire`, inside the fire itself -
   * which is why this can afford to believe where the client is pointing.
   */
  spark(x: number, y: number, z: number): boolean {
    if (this.fire.isBurning(x, y, z)) return false;
    return this.fire.light(x, y, z, this.world.getBlock(x, y, z), false);
  }

  /** A вогняний снаряд, likewise. */
  throwBolt(from: { x: number; y: number; z: number },
    dir: { x: number; y: number; z: number }): boolean {
    if (this.bolts.length >= MAX_BOLTS) return false;
    this.bolts.push(makeFirebolt(from, dir, 1));
    return true;
  }

  /**
   * Grow the country the people are standing in, and stock it.
   *
   * The кремль and the hill exist all at once and need none of this. An
   * adventure is a thousand blocks across and only what somebody has walked
   * into is real, and until now the *server* had walked nowhere: it grew the
   * patch round the start when the world was made and never another column.
   * Everything that lives on the server therefore had nowhere to stand
   * except that first patch - the herd sat in it, and there were no
   * вовкулаки and no мавки at all, because the country they need had never
   * been generated on this side of the wire.
   *
   * The clients grow their own terrain from the seed, so none of this is
   * sent anywhere. It exists so that the things the server owns have ground
   * under them.
   */
  private roam(dt: number): void {
    if (this.map.terrain !== "adventure") return;
    const world = this.grownWorld;
    if (world === null) return;

    const people: Person[] = [];
    for (const client of this.clients.values()) {
      if (client.ready) people.push({ x: client.x, z: client.z });
    }
    if (people.length === 0) return;

    /*
     * A few columns a tick, for each of them.
     *
     * The same budget the page uses, and for the same reason: a column is
     * about a millisecond, and a server that grew a whole horizon in one
     * tick would drop every other world on the machine while it did.
     */
    for (const who of people) world.ensureAround(who.x, who.z, GROW_REACH);

    this.sweepClock += dt;
    if (this.sweepClock < 1) return;
    this.sweepClock = 0;
    world.forgetBeyond(people, FORGET_REACH);
    this.restock(people);
  }

  /**
   * Let go of what nobody is near, and put down what is missing.
   *
   * The rules are core/entity/restock.ts's and the page runs exactly the
   * same ones. What is here is the half only this side can do: turning "a
   * свиня, here" into one of the свині this realm owns and will broadcast.
   */
  private restock(people: readonly Person[]): void {
    const world = this.grownWorld;
    if (world === null) return;
    const dark = this.cycle.phase === "night" || this.cycle.phase === "dusk";

    for (let i = this.beasts.length - 1; i >= 0; i--) {
      if (strayedFromAll(this.beasts[i]!, people, LIFE_KEEP)) {
        this.beasts.splice(i, 1);
      }
    }
    for (let i = this.fish.length - 1; i >= 0; i--) {
      if (strayedFromAll(this.fish[i]!, people, LIFE_KEEP)) {
        this.fish.splice(i, 1);
      }
    }
    for (let i = this.orcs.length - 1; i >= 0; i--) {
      const foe = this.orcs[i]!;
      if (!WILD_FOES.includes(foe.breed.kind)) continue;
      if (nightOnly(foe.breed.kind) && !dark) { this.orcs.splice(i, 1); continue; }
      // And the day one at dusk: the two are one мавка in two skins.
      if (dayOnly(foe.breed.kind) && dark) { this.orcs.splice(i, 1); continue; }
      if (strayedFromAll(foe, people, FOES_KEEP)) this.orcs.splice(i, 1);
    }

    const country: Wood = {
      groundAt: (x, z) => world.groundAt(x, z),
      blockAt: (x, y, z) => world.getBlock(x, y, z),
      fluidAt: this.fluidAt,
      inside: (x, z) => world.withinBounds(x, 1, z),
      leafAt: (x, y, z) => isLeaf(world.getBlock(x, y, z)),
    };
    /*
     * Around each of them, counting only what is near *that* person.
     *
     * A world-wide count would stock whoever happened to be walked to first
     * and leave everybody else in an empty country: three people standing a
     * thousand blocks apart are three countries, and each of them should
     * have animals in it.
     */
    for (const who of people) {
      const near = (e: { x: number; z: number }, keep: number): boolean =>
        !strayedFromAll(e, [who], keep);
      for (const put of whatToPutDown(country, who, {
        beasts: this.beasts.filter((b) => near(b, LIFE_KEEP)).length,
        fish: this.fish.filter((f) => near(f, LIFE_KEEP)).length,
        wolves: this.orcs.filter(
          (o) => o.breed.kind === "vovkulaka" && near(o, FOES_KEEP)).length,
        mavkas: this.orcs.filter(
          (o) => (o.breed.kind === "mavka" || o.breed.kind === "mavkaNight") && near(o, FOES_KEEP)).length,
        turn: this.beasts.length,
      }, world.seaLevel, BlockId.Air, dark)) {
        const at = { x: put.x, y: put.y, z: put.z };
        if (put.what === "karas") this.fish.push(makeFish(at));
        else if (put.what === "vovkulaka" || put.what === "mavka" || put.what === "mavkaNight") {
          this.orcs.push(makeFoe(at, put.what));
        } else this.beasts.push(makeBeast(put.what as BeastKind, at));
      }
    }
  }

  /** Everybody an orc might go for. */
  private orcTargets(): OrcTarget[] {
    const out: {
      id: number; x: number; y: number; z: number; flying: boolean;
    }[] = [];
    for (const client of this.clients.values()) {
      if (!client.ready) continue;
      out.push({
        id: client.id, x: client.x, y: client.y, z: client.z,
        flying: client.flying,
      });
    }
    // The same rule the lone game uses, out of the same function: somebody
    // in creative is not on the menu.
    return huntable(out);
  }

  /**
   * One step of this world.
   *
   * Only called while somebody is in it. A world nobody is standing in does
   * not need its fire to spread or its clocks to run - and if it did, every
   * map on the server would cost a tick whether or not anybody had ever
   * opened it.
   */
  tick(dt: number): void {
    if (this.grownWorld === null) return;
    const world = this.grownWorld;

    /*
     * The hour. On a hill it is always noon, and the client knows it too -
     * but the clock is this world's either way, so there is one answer to
     * "is it dark" and everybody gets it.
     */
    if (this.map.terrain === "hill") {
      // A hill is always noon, and stays there: the map is five minutes long
      // and a sunset in the middle of it would be a sunset nobody asked for.
      this.cycle.t = 0.5;
      this.cycle.timeScale = 0;
    }
    this.cycle.update(dt);

    // Only where there is a hill to be king of.
    if (this.map.terrain === "hill") this.stepCrown(dt);
    this.stepWar(dt);
    this.stepShells(dt);
    /*
     * And a танк goes where its driver goes.
     *
     * The page drives it and its position arrives with the driver's own
     * `move`, exactly as a boat's does - so all that happens here is that
     * the machine is put where the person in it is.
     */
    for (const tank of this.tanks) {
      if (tank.rider === null) continue;
      const driver = this.clients.get(tank.rider);
      if (driver === undefined || !driver.ready) { tank.rider = null; continue; }
      tank.x = driver.x;
      tank.y = driver.y - TANK_FLOOR;
      tank.z = driver.z;
    }

    this.roam(dt);

    /*
     * And the water on its way, which is the server's too.
     *
     * Every cell that arrives is an ordinary edit, so it goes out to
     * everybody with the rest of them - a trench filling is the same shape
     * of message as somebody digging one.
     */
    const ran = world.stepFluids(dt);
    if (ran.length > 0) {
      this.pending.push(...ran);
      this.dirtyWorld = true;
    }

    /*
     * And every boat with somebody in it is wherever they are.
     *
     * Not stepped: the rider's own page runs the oars and tells us where
     * that put them, the same way it tells us where walking put them. Doing
     * it here as well would be two hulls in one place disagreeing.
     */
    for (const boat of this.boats) {
      if (boat.rider === null) continue;
      const who = this.clients.get(boat.rider);
      if (who === undefined || !who.ready) { boat.rider = null; continue; }
      boat.x = who.x;
      boat.y = who.y;
      boat.z = who.z;
    }

    /*
     * The herd, the карасі and the villagers.
     *
     * Nothing any of them does can hurt anybody, so there is nothing to
     * answer for - only the ones that went down, and what they leave.
     */
    const herd = stepBeasts(
      this.beasts, dt, this.solidAt, this.fluidAt, Math.random, this.company());
    for (const bite of herd.bites) this.biteFor(bite);
    for (const fallen of herd.fallen) {
      this.broadcast({
        t: "fell", x: Math.floor(fallen.x), y: Math.floor(fallen.y),
        z: Math.floor(fallen.z), drops: rollBeastDrop(fallen),
      });
    }
    for (const caught of stepFish(this.fish, dt, this.fluidAt)) {
      this.broadcast({
        t: "fell", x: Math.floor(caught.x), y: Math.floor(caught.y),
        z: Math.floor(caught.z), drops: [{ item: KARAS_DROP, count: 1 }],
      });
    }
    tickVillagers(this.folk, dt);

    // ---- the raid --------------------------------------------------------
    const standing = world.standingLairs().length;
    if (standing < this.lairsLeft) this.lairsLeft = standing;
    if (this.raid.step(dt, standing) === "raid") {
      this.landRaid(this.raid.waveSize(standing, this.orcs.length));
    }
    /*
     * And the slow drip between raids. A fortress left alone fills back up to
     * a garrison and stops; the ceiling is counted per lair, which is what
     * makes breaking one worth the trouble.
     */
    for (const sent of this.lairClock.step(dt, world.standingLairs(),
      (lair) => this.orcs.filter((o) => o.from !== undefined &&
        o.from.x === lair.x && o.from.y === lair.y && o.from.z === lair.z).length)) {
      if (this.orcs.length >= MAX_ORCS) break;
      this.orcs.push(makeOrc(
        world.findSpawn(sent.at.x + 2, sent.at.z + 2),
        this.orcs.length % 3 === 2 ? "archer" : "sabre",
        sent.at,
      ));
    }

    /*
     * The bots, on the one map that has any.
     *
     * "Команда гравців одна, а навпроти них боти." They are orcs - the army
     * this game already has - and what makes them a *team* rather than a
     * raid is three rules: they are born in their own quarter, they walk at
     * the enemy's flag rather than wandering, and they can bring it down.
     * That last one matters more than it looks: without it a bot match is
     * one nobody can lose.
     */
    const botMarch = this.stepBots(dt);

    const turn = stepOrcs(
      this.orcs, dt, this.solidAt, this.orcTargets(), botMarch, this.sapperDig);
    for (const blow of turn.blows) {
      // What their armour does about it is theirs to work out: armour,
      // falling and drowning are all still the body's own business.
      const hit = this.clientById(blow.at);
      if (hit !== undefined) hit.send({ t: "hurt", amount: blow.damage });
    }
    for (const shot of turn.shots) {
      this.orcArrows.push(makeArrow(shot.from, shot.dir));
      this.broadcast({
        t: "arrow", x: shot.from.x, y: shot.from.y, z: shot.from.z,
        dx: shot.dir.x, dy: shot.dir.y, dz: shot.dir.z,
      });
    }
    /*
     * And his cold fire, which everybody has to see coming.
     *
     * Told rather than predicted: the server owns where an orc is and what
     * it threw, and a ball of fire each page invented for itself would be
     * three different balls of fire in three different places.
     */
    for (const hurl of turn.hurls) {
      this.bolts.push(makeFirebolt(hurl.from, hurl.dir, COLD_SPEED, true));
      this.broadcast({
        t: "bolt", x: hurl.from.x, y: hurl.from.y, z: hurl.from.z,
        dx: hurl.dir.x, dy: hurl.dir.y, dz: hurl.dir.z, cold: true,
      });
    }
    for (const dead of turn.fallen) {
      /*
       * The roll is the server's. Left to each client, everybody would find
       * different gold on the same body - which is worse than nobody finding
       * any, because both people are looking at the same corpse.
       */
      this.broadcast({
        t: "fell", x: Math.floor(dead.x), y: Math.floor(dead.y), z: Math.floor(dead.z),
        drops: rollOrcDrops(dead.breed),
      });
    }
    // Their arrows. Only players are targets: an orc's arrow never hits an orc.
    for (const hit of stepArrows(this.orcArrows, dt, this.solidAt,
      [...this.clients.values()].filter((c) => c.ready))) {
      hit.target.send({ t: "hurt", amount: ARROW_DAMAGE });
    }
    /*
     * And the players', against everything alive.
     *
     * Whoever it fells is reported by the next step of whichever list they
     * were in, so the drops travel the one way they ever do. An arrow out of
     * the dark sends an animal away from the archer rather than from the
     * arrow: what it runs from is whoever it can work out is there.
     */
    for (const hit of stepArrows(this.playerArrows, dt, this.solidAt,
      [...this.orcs, ...this.beasts, ...this.folk])) {
      if (hit.target.vitals.hurt(ARROW_DAMAGE) <= 0) continue;
      /*
       * Sent away from the archer, not from the arrow.
       *
       * An animal hit out of the dark runs from whoever it can work out is
       * there - and its position now is inside the animal, so running from
       * that is running on the spot. Back along the flight is where the shot
       * came from, which is the only thing an arrow knows about the archer
       * and is exactly the thing that is wanted.
       */
      const from = {
        x: hit.arrow.x - hit.arrow.vx,
        z: hit.arrow.z - hit.arrow.vz,
      };
      const beast = this.beasts.find((b) => b === hit.target);
      if (beast !== undefined) spook(beast, from.x, from.z);
      const folk = this.folk.find((v) => v === hit.target);
      if (folk !== undefined) {
        faceToward(folk, from.x, from.z);
        folk.speaking = SPEECH_SECONDS;
        folk.said++;
      }
    }

    for (const impact of stepFirebolts(this.bolts, dt, this.solidAt)) {
      this.fire.light(impact.x, impact.y, impact.z,
        world.getBlock(impact.x, impact.y, impact.z), true);
    }
    const burnt = this.fire.step(dt, (x, y, z) => world.getBlock(x, y, z));
    for (const cell of burnt.consumed) {
      world.setBlock(cell.x, cell.y, cell.z, 0);
      this.pending.push({ x: cell.x, y: cell.y, z: cell.z, id: 0 });
      this.dirtyWorld = true;
    }

    // One building a tick, before anything else, so what everybody is told
    // about the world in this snapshot includes it.
    this.raiseNext();
    if (this.pending.length > 0) {
      this.broadcast({ t: "blocks", edits: this.pending });
      this.pending = [];
    }
    this.broadcast({
      t: "orcs",
      list: this.orcs.map((o) => ({
        id: o.id, kind: o.breed.kind, x: o.x, y: o.y, z: o.z, yaw: o.yaw,
        // Only while it is showing one, which is a fifth of a second after a
        // hit and never otherwise.
        ...(o.vitals.glow > 0 ? { hurt: true } : {}),
        ...(o.struck > 0 ? { blows: o.struck } : {}),
      })),
    });
    /*
     * The herd every other tick.
     *
     * Beasts, fish, boats and folk are the longest message and the slowest
     * movers, and the page draws them along a trail between the last two
     * places it heard, so seven and a half a second reads as fifteen did.
     * Orcs and players stay at the full rate: those are the ones swinging
     * at you.
     */
    this.ticks++;
    if (this.ticks % 2 === 0) this.sendHerd();

    /*
     * And the hour, rarely.
     *
     * A correction, not a heartbeat: both ends run the same clock at the
     * same speed, so the sky moves smoothly between these and this only has
     * to catch a page that was asleep in a background tab. Fifteen seconds
     * of game time is about a minute and a half of sky at this day length,
     * which nobody can see, and a tab that was asleep for an hour is put
     * straight the moment it wakes.
     */
    this.skyClock += dt;
    if (this.skyClock >= SKY_EVERY) {
      this.skyClock = 0;
      this.broadcast({ t: "sky", time: this.cycle.t });
    }

    /*
     * And the changed ground, a little at a time.
     *
     * Worked out once and handed to everybody, because `modifiedChunks` walks
     * the whole world and doing it per person would be that walk times
     * sixteen. Skipped entirely when everybody already has everything, which
     * on a world nobody has dug in is always.
     */
    let changed: readonly Chunk[] | null = null;
    for (const client of this.clients.values()) {
      if (!client.ready) continue;
      changed ??= world.modifiedChunks();
      this.streamChunks(client, changed);
    }
  }

  /**
   * The herd, to whoever is near enough to see it.
   *
   * Forty карасі and fourteen pigs in every snapshot to every player is
   * fifteen times a second of animals nobody is looking at. None of them has
   * ever been a threat, so a player learning about a pig when they come over
   * the hill loses nothing - which is not true of an orc, and is why the orcs
   * are still sent whole.
   */
  private sendHerd(): void {
    /*
     * Each animal written once, then each player is handed the pieces
     * that are near them. See core/net/snapshot: with twenty players this
     * was twenty times the same forty carp through JSON.stringify.
     */
    const at = <T extends { x: number; z: number }>(list: readonly T[]) =>
      list.map((c) => ({ x: c.x, z: c.z }));
    const beasts = pieces(this.beasts.map((b) => ({
      id: b.id, kind: b.breed.kind, x: b.x, y: b.y, z: b.z, yaw: b.yaw,
      ...(b.vitals.glow > 0 ? { hurt: true } : {}),
    })));
    const fish = pieces(this.fish.map((f) => ({
      id: f.id, x: f.x, y: f.y, z: f.z, yaw: f.yaw, pitch: f.pitch,
      ...(f.vitals.glow > 0 ? { hurt: true } : {}),
    })));
    const tanks = pieces(this.tanks.map((t) => ({
      id: t.id, x: t.x, y: t.y, z: t.z, yaw: t.yaw,
      hits: t.hits, rider: t.rider,
    })));
    const boats = pieces(this.boats.map((b) => ({
      id: b.id, x: b.x, y: b.y, z: b.z, yaw: b.yaw, rider: b.rider,
    })));
    const folk = pieces(this.folk.map((v) => ({
      id: v.id, name: v.name, skinId: v.skinId,
      x: v.x, y: v.y, z: v.z, yaw: v.yaw, line: v.line,
      ...(v.said > 0 ? { said: v.said } : {}),
      ...(v.vitals.glow > 0 ? { hurt: true } : {}),
    })));
    const where = {
      beasts: at(this.beasts), fish: at(this.fish), tanks: at(this.tanks),
      boats: at(this.boats), folk: at(this.folk),
    };

    for (const client of this.clients.values()) {
      if (!client.ready) continue;
      const near = <T>(all: readonly T[], spots: readonly { x: number; z: number }[]) =>
        all.filter((_, i) =>
          Math.hypot(spots[i]!.x - client.x, spots[i]!.z - client.z) <= HERD_SIGHT);
      const fields: Field[] = [
        ["beasts", near(beasts, where.beasts)],
        ["fish", near(fish, where.fish)],
        // Only when there are any: a field that is usually empty is left out.
        ...(this.tanks.length > 0 ? [["tanks", near(tanks, where.tanks)] as Field] : []),
        ["boats", near(boats, where.boats)],
        ["folk", near(folk, where.folk)],
      ];
      client.sendEncoded(snapshotObject("herd", fields), snapshotText("herd", fields));
    }
  }

  /**
   * Write this world down, if there is anything to write and we are allowed.
   *
   * @returns whether it went.
   */
  save(why: string): boolean {
    /*
     * The names and bags are written whether or not there is a world in
     * memory: somebody can claim a name in a world that has since been
     * cleared away, and that claim still has to survive.
     */
    if (!this.maySave) return false;
    /*
     * The bags go whether or not the ground moved.
     *
     * They are the thing most likely to have changed while nothing was dug -
     * an afternoon of crafting changes no blocks at all - so hanging them on
     * the world's own dirty flag would lose exactly the sessions that were
     * only about the bag.
     */
    if (this.namesDirty) {
      try {
        saveNamesTo(this.keep, this.names);
        this.namesDirty = false;
      } catch (error) {
        console.error(`${this.id}  names could not be saved: ${String(error)}`);
      }
    }
    if (this.bagsDirty) {
      try {
        saveBagsTo(this.keep, this.bags);
        this.bagsDirty = false;
      } catch (error) {
        console.error(`${this.id}  bags could not be saved: ${String(error)}`);
      }
    }
    /*
     * And the човни, which go with the changed ground and not with the bags.
     *
     * `dirtyWorld` is already set by launching one and by taking one out of
     * the water, and there is nothing else that moves a boat somebody is not
     * standing in - so a boat rowed across a lake and left there is written
     * down with the next chunk that changes, which is near enough.
     */
    if (this.dirtyWorld) {
      try {
        saveBoatsTo(this.keep, this.boats.map(
          (b) => ({ x: b.x, y: b.y, z: b.z, yaw: b.yaw })));
      } catch (error) {
        console.error(`${this.id}  boats could not be saved: ${String(error)}`);
      }
    }
    if (this.dirtyMeta && this.maySave) {
      try {
        saveMetaTo(this.keep, { oneBlockBreaks: this.oneBlockBreaks });
        this.dirtyMeta = false;
      } catch (error) {
        console.error(`${this.id}  meta could not be saved: ${String(error)}`);
      }
    }
    const world = this.grownWorld;
    if (world === null || !this.dirtyWorld) return false;
    try {
      const chunks = saveWorldTo(world, this.keep);
      this.dirtyWorld = false;
      console.log(`${this.id}  saved ${chunks} chunks (${why})`);
      return true;
    } catch (error) {
      // A failed write is worth shouting about and not worth dying for: the
      // world in memory is still fine and people are still playing in it.
      console.error(`${this.id}  could not be saved: ${String(error)}`);
      return false;
    }
  }

  /**
   * Put the blocks away and forget the raid.
   *
   * Saved first. What goes is what does not survive a deployment either: the
   * orcs, the clocks, whatever is in the air and whatever is burning. The
   * world itself comes back from the seed and the file the moment somebody
   * asks for this map again.
   */
  clearAway(): void {
    if (this.grownWorld === null) return;
    this.save("clearing away an empty world");
    this.grownWorld = null;
    this.orcs = [];
    this.orcArrows = [];
    this.playerArrows = [];
    this.bolts = [];
    this.fire = new Fire();
    this.raid = new RaidClock();
    this.lairClock = new LairClock();
    this.queued = [];
    this.pending = [];
    this.lairsLeft = 0;
    console.log(`${this.id}  cleared away`);
  }
}
